import { getRequestPeriods, getSelectedSiteIds, isRetailMediaTemplate } from './shared';

import { CMS_IMPRESSION_VIEWS } from './cms';
export { CMS_IMPRESSION_VIEWS } from './cms';

const CMS_NAMES = {
  BROADSIGN: 'Broadsign',
  BRIGHTAUTHOR: 'Brightauthor',
  SCALA: 'Scala',
  HIVESTACK: 'Hivestack',
  AYUDA: 'Ayuda',
  SIGNAGELIVE: 'Signagelive'
};

// Normalize both legacy integration IDs and site vendor names, then deduplicate by vendor.
export const getProjectCmsVendors = (project, sites) => {
  const values = [
    ...(project?.enabledCmsIntegrations || []),
    ...(project?.enabledIntegrations || []),
    ...sites.flatMap(site => site.cmsVendors || [])
  ];
  return Array.from(new Set(values.map(value => {
    const normalized = String(value || '').trim().toUpperCase();
    return Object.keys(CMS_NAMES).find(vendor => normalized === vendor ||
      new RegExp(`^${vendor}(FACE|VEHICLE)V[0-9]+$`).test(normalized));
  }).filter(Boolean))).sort();
};

export const getMobileCmsTemplate = (templates, vendor) => {
  // The current API supports these impression views only for Broadsign Mobile.
  if (vendor !== 'BROADSIGN') return null;
  const compatible = (templates || []).filter(template => template.enabled !== false &&
    (!template.collection || template.collection === 'ANALYTICS') &&
    template.compatibleDatasources?.includes('MOBILE_M') && (template.id || template.name));
  return compatible.find(template => template.isCms && template.cmsId?.toUpperCase() === vendor) ||
    compatible.find(template => !template.isCms) || null;
};

export const getCmsImpressionWidgets = ({ dashboard, project, sites = [], templates = [] }) => {
  if (!dashboard || dashboard.isCms ||
    !(dashboard.headers === 'mobile' || isRetailMediaTemplate(dashboard))) return [];
  return getProjectCmsVendors(project, sites).flatMap(vendor => {
    const template = getMobileCmsTemplate(templates, vendor);
    return CMS_IMPRESSION_VIEWS.map(view => ({
      ...view,
      id: `${vendor.toLowerCase()}_${view.id}`,
      title: view.title.replace('CMS impressions', `${CMS_NAMES[vendor]} Impressions`),
      viewId: view.id,
      cmsId: vendor.toLowerCase(),
      cmsImpressions: true,
      cmsTemplateId: template?.id || template?.name?.replace(/^templates\//, ''),
      display: 'table'
    }));
  });
};

const unique = values => Array.from(new Set(values.filter(Boolean)));
const sourceNames = values => unique((values || []).map(value =>
  typeof value === 'string' ? value : value.name || value.displayName));

export const getCmsSelectedSites = (sites = [], cmsId) => sites.filter(site =>
  !cmsId || !Array.isArray(site?.cmsVendors) ||
  site.cmsVendors.some(vendor => vendor.toLowerCase() === cmsId.toLowerCase()));

export const buildCmsImpressionsRequest = ({ projectId, templateId, filters, cmsId }) => {
  const selection = {};
  const siteIds = getSelectedSiteIds(getCmsSelectedSites(filters.selectedSites, cmsId));
  const selectedAssets = filters.selectedAssets;
  const cmsPlayerIds = unique(Array.isArray(selectedAssets)
    ? selectedAssets.filter(asset => siteIds.includes(asset.siteId || asset.data?.siteId))
      .flatMap(asset => asset.cmsPlayerIds || [])
    : filters.cmsPlayerIds || []);
  const forVendor = values => (values || []).filter(value => !cmsId || !value.cmsId || value.cmsId === cmsId);
  const campaignIds = sourceNames(forVendor(filters.selectedCampaigns || filters.selectedAdvertisers));
  const mediaIds = sourceNames(forVendor(filters.selectedMedias));
  Object.entries({ siteIds, cmsPlayerIds, campaignIds, mediaIds }).forEach(([key, values]) => {
    if (values.length) selection[key] = values;
  });
  const viewFilters = {};
  if (filters.filterByWeekday?.length) viewFilters.weekdays = filters.filterByWeekday;
  if (filters.filterByTime?.start && filters.filterByTime?.end) {
    viewFilters.timeOfDay = { start: filters.filterByTime.start, end: filters.filterByTime.end };
  }
  return {
    scope: { projectId },
    context: {
      templateId,
      collection: 'ANALYTICS_COLLECTION_ANALYTICS',
      datasource: 'CMS_DATASOURCE_MOBILE'
    },
    period: getRequestPeriods(filters, filters.timezone).current,
    ...(Object.keys(selection).length ? { selection } : {}),
    ...(Object.keys(viewFilters).length ? { filters: viewFilters } : {})
  };
};

export const getCmsImpressionsTableData = (response, fields) => ({
  datasets: [{
    label: 'Impressions',
    value: (response?.categorySeries?.points || []).map(point => Object.fromEntries(fields.map(field => [
      field,
      field === 'impressions' ? (point.values?.impressions ?? point.value ?? '0') :
        field === 'site' && point.values?.site_name ? point.values.site_name :
        (point.values?.[field] ?? point[field] ?? point.label ?? point.key)
    ])))
  }]
});
